<template>
  <div class="da">
    <header class="da__header">
      <div>
        <h1 class="da__title">Delegate Agreements</h1>
        <p class="da__subtitle">
          Prescribers who have authorized your team to submit prior authorizations on their behalf.
        </p>
      </div>
      <span v-if="count" class="da__count">{{ count }} {{ count === 1 ? "prescriber" : "prescribers" }}</span>
    </header>

    <section class="da__search">
      <div class="da__field">
        <v-icon icon="mdi-magnify" size="20" class="da__field-icon" />
        <input
          v-model="search"
          type="text"
          class="da__input"
          placeholder="Search by name or NPI"
          aria-label="Search delegate agreements by name or NPI"
          @input="onSearchInput" />
      </div>
    </section>

    <div v-if="errors.length" class="da__toast da__toast--error">
      <div v-for="error in errors" :key="error">{{ error }}</div>
    </div>

    <section class="da__table">
      <div class="da__table-head">
        <span>Provider</span>
        <span>NPI</span>
      </div>

      <div v-if="loading" class="da__empty">
        <v-progress-circular indeterminate color="primary" size="28" width="3" />
      </div>
      <div v-else-if="!items.length" class="da__empty">
        {{ search ? `No prescribers match "${search}".` : "No delegate agreements yet." }}
      </div>

      <div v-for="item in items" v-else :key="item.id" class="da__row">
        <div class="da__col--provider">
          <span class="da__provider-name">{{ providerName(item) }}</span>
        </div>
        <div class="da__mono">{{ item.providerNpiNumber || "—" }}</div>
      </div>
    </section>

    <div v-if="pageCount > 1" class="da__pagination">
      <v-pagination
        :model-value="page"
        :length="pageCount"
        :total-visible="7"
        density="comfortable"
        rounded="circle"
        @update:model-value="onPageChange" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import providerDelegatesService from "@/services/providerDelegatesService";
import { tryParseApiErrors } from "@/utils";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const items = ref([]);
const count = ref(0);
const page = ref(1);
const search = ref("");
const loading = ref(false);
const errors = ref([]);

const pageCount = computed(() => Math.max(1, Math.ceil(count.value / PAGE_SIZE)));

const providerName = (item) => `${item.providerFirstName || ""} ${item.providerLastName || ""}`.trim() || "—";

const fetchAgreements = async () => {
  loading.value = true;
  errors.value = [];
  try {
    const data = await providerDelegatesService.list({ page: page.value, pageSize: PAGE_SIZE, search: search.value });
    items.value = data.results;
    count.value = data.count;
  } catch (error) {
    // A shrinking result set can leave the current page out of range (DRF 404); clamp to page 1.
    if (error?.response?.status === 404 && page.value > 1) {
      page.value = 1;
      await fetchAgreements();
      return;
    }
    errors.value = tryParseApiErrors(error);
  } finally {
    loading.value = false;
  }
};

let searchTimer = null;
const onSearchInput = () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    page.value = 1;
    fetchAgreements();
  }, SEARCH_DEBOUNCE_MS);
};

const onPageChange = (next) => {
  page.value = next;
  fetchAgreements();
};

onMounted(fetchAgreements);
onBeforeUnmount(() => clearTimeout(searchTimer));
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

$da-border: 1px solid rgba(#042143, 0.08);
$da-grid: minmax(240px, 3fr) minmax(140px, 1fr);

.da {
  max-width: 1060px;
  margin: 0 auto;
  padding: 40px 40px 56px;
  font-family: $lh_font_family_dm_sans;
  color: $lh_color_navy_dark;
}

.da__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.da__title {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.da__subtitle {
  margin-top: 6px;
  color: $lh_color_blue_gray;
  font-size: 13px;
}

.da__count {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: $lh_color_blue_gray;
  background: white;
  border: $da-border;
  border-radius: 999px;
  padding: 6px 14px;
  white-space: nowrap;
}

.da__search {
  margin-bottom: 20px;
}

.da__field {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  background: white;
  border: $da-border;
  border-radius: 12px;
  transition: box-shadow 0.15s, border-color 0.15s;

  &:focus-within {
    border-color: $lh_color_blue;
    box-shadow: 0 0 0 3px rgba(#2470ca, 0.12);
  }
}

.da__field-icon {
  color: $lh_color_blue_gray;
}

.da__input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  padding: 10px 0;
  font-family: $lh_font_family_dm_sans;
  font-size: 14px;
  color: $lh_color_navy_dark;

  &::placeholder {
    color: $lh_color_blue_gray;
  }
}

.da__toast {
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 13px;
  margin-bottom: 16px;
}
.da__toast--error {
  background: rgba(#e83d52, 0.08);
  color: darken($lh_color_red, 6%);
}

.da__table {
  background: white;
  border: $da-border;
  border-radius: 16px;
  box-shadow: $lh_shadow_sm;
  overflow-x: auto;
  overflow-y: hidden;
}

.da__table-head,
.da__row {
  display: grid;
  grid-template-columns: $da-grid;
  min-width: 444px; // track minimums (240 + 140) + column gap (16) + padding (48) — scroll rather than clip on a narrow content area
  align-items: center;
  column-gap: 16px;
  padding: 0 24px;
}

.da__table-head {
  height: 44px;
  background: $lh_color_snowy_white;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: $lh_color_blue_gray;
}

.da__row {
  min-height: 60px;
  border-top: $da-border;
  transition: background 0.12s;
  &:hover {
    background: #fafbfd;
  }
}

.da__col--provider {
  min-width: 0;
}
.da__provider-name {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.da__mono {
  font-family: $lh_font_mono;
  font-size: 13px;
  color: $lh_color_navy_dark;
}

.da__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: $lh_color_blue_gray;
  font-size: 14px;
}

.da__pagination {
  margin-top: 20px;
}
</style>
